import moment from 'moment/moment'

import { DataType } from '../types/DataType'
import { ConstructorArgs } from '../types/models'
import { formatTimeStamp } from '../utils/dates'
import { currencyFloat, currencyFormat } from '../utils/numbers'
import { Adjustment } from './Adjustment'
import { BaseRecord } from './BaseRecord'
import { Booking } from './Booking'
import { Charge } from './Charge'
import { Estimate } from './Estimate'
import { Gift } from './Gift'
import { Invoice } from './Invoice'
import { Item } from './Item'
import { Job } from './Job'
import { Lesson } from './Lesson'
import { Option } from './Option'
import { Order } from './Order'
import { Package } from './Package'
import { Royalty } from './Royalty'
import { FKType } from './SQLBuilder'
import { Subaccount } from './Subaccount'
import { Subscription } from './Subscription'
import { Transaction } from './Transaction'

export type LineConstructorArgs = ConstructorArgs<() => Line>

export class Line extends BaseRecord {
    readonly recordType = 'line'

    trantype?: string
    type?: string

    date?: moment.Moment
    total?: number

    category?: string

    memo?: string
    rate?: number
    quantity?: number
    weight?: number
    seqnum?: number

    taxable?: boolean // settable always except on bookings where it's auto-calculated (always on for products, on for services if tax is enabled)
    discountable?: boolean // settable always except on bookings where it's auto-calculated (always off for products, otherwise on package pending)
    lastModifiedDate?: moment.Moment

    itemDisplay?: string

    constructor(data?: Partial<LineConstructorArgs>) {
        super(data)
        if (!data) {
            return
        }

        if (data.trantype) {
            this.trantype = data.trantype
        }
        else if (data.trandoc && typeof data.trandoc !== 'string') {
            this.trantype = Line.modelToTrantype(data.trandoc)
        }
        this.type = data.type ?? undefined
        this.total = this.propertyFromCurrencyArg(data.total)
        this.memo = data.memo ?? undefined
        this.category = data.category ?? undefined
        this.date = this.propertyFromTimestampArg(data.date)
        this.rate = this.propertyFromCurrencyArg(data.rate)
        this.seqnum = this.propertyFromIntArg(data.seqnum)
        this.weight = this.propertyFromNumberArg(data.weight)
        this.quantity = this.propertyFromNumberArg(data.quantity)
        this.taxable = data.taxable ?? false
        this.discountable = data.discountable ?? false
        this.lastModifiedDate = this.propertyFromTimestampArg(data.last_modified_date)
        this.itemDisplay = data.item_display ?? undefined
    }

    clone(overrides?: Partial<LineConstructorArgs>): Line {
        return new Line({ ...this.serializeForClone(), ...overrides })
    }

    static initWithType(type: string, trandoc: Transaction): Line {
        const line = new Line()
        line.type = type
        line.discountable = true
        line.date = moment().startOf('day')
        line.trandoc = trandoc
        return line
    }

    static initWithBooking(booking: Booking, trandoc: Transaction): Line {
        const line = Line.initWithType('booking', trandoc)
        line.booking = booking
        line.total = booking.owedAmount
        line.date = booking.date
        line.memo = booking.memo
        line.taxable = !!booking.taxcode && booking.taxAmount > 0
        return line
    }

    static initWithJob(job: Job, trandoc: Transaction): Line {
        const line = Line.initWithType('job', trandoc)
        line.job = job
        line.total = job.amount
        const booking: Booking | undefined = job.booking
        if (booking) {
            line.date = booking.date
            line.memo = booking.memo
        }
        else {
            const lesson: Lesson | undefined = job.lesson
            if (lesson) {
                line.date = lesson.date
                line.memo = lesson.memo
            }
            else {
                const instance: any = job.record
                if (instance) {
                    line.memo = instance.memo
                    line.date = instance.paidDate
                }
            }
        }
        return line
    }

    static initWithLine(line: Line, trandoc: Transaction): Line {
        const newLine = Line.initWithType(line.type!, trandoc)
        newLine.total = line.total
        newLine.memo = line.memo
        newLine.date = line.date
        newLine.taxable = line.taxable
        newLine.discountable = line.discountable
        const item: Item | undefined = line.item
        if (item) {
            newLine.item = item
            newLine.rate = line.rate
            newLine.weight = line.weight
            newLine.quantity = line.quantity ?? 1.0
            newLine.weight = line.weight
        }
        else {
            newLine.category = line.category
        }
        return newLine
    }

    static initWithItem(item: Item, trandoc: Transaction): Line {
        const line = Line.initWithType('item', trandoc)
        line.item = item
        line.quantity = 1.0
        line.memo = item.memo
        line.rate = item.rate
        line.total = item.rate
        if (item.type === 'product') {
            line.weight = item.weight
        }
        line.taxable = item.type === 'product' || item.allowSalesTax
        line.discountable = trandoc instanceof Order || item.type !== 'product'
        return line
    }

    get amount(): number | undefined {
        if (this.discountable) {
            const trandoc = this.trandoc
            if (trandoc && trandoc.discount && trandoc.discount > 0) {
                const discountableTotal = trandoc.discountableTotal!
                return discountableTotal > 0
                    ? (this.total ?? 0) *
                          (1 - currencyFloat(trandoc.discountAmount! / discountableTotal))
                    : 0
            }
        }
        return this.total
    }

    static categoryStrings(): Option[] {
        const strings = []
        strings.push(new Option(undefined, { id: 'supplies', label: 'Supplies' }))
        strings.push(new Option(undefined, { id: 'labor', label: 'Labor' }))
        strings.push(new Option(undefined, { id: 'mileage', label: 'Gas & mileage' }))
        strings.push(new Option(undefined, { id: 'meals', label: 'Meals & entertainment' }))
        strings.push(new Option(undefined, { id: 'travel', label: 'Travel' }))
        strings.push(new Option(undefined, { id: 'fees', label: 'Fees' }))
        strings.push(new Option(undefined, { id: 'adjustments', label: 'Adjustments' }))
        return strings
    }

    toString(): string {
        if ('expense' === this.type)
            return [
                this.type,
                this.category,
                this.date?.format('MM/DD'),
                currencyFormat(this.total),
                this.memo,
            ].join(', ')
        else if ('item' === this.type)
            return [
                this.type,
                this.item?.name ?? '',
                this.rate,
                this.quantity,
                currencyFormat(this.total),
            ].join(', ')
        else if ('job' === this.type)
            return [this.type, this.job?.booking?.title, currencyFormat(this.total)].join(', ')
        else if ('custom' === this.type)
            return [this.type, this.memo, this.quantity, currencyFormat(this.total)].join(', ')
        else return [this.type, this.booking?.title, currencyFormat(this.total)].join(', ')
    }

    hasChangesFrom(otherLine: Line): boolean {
        if (BaseRecord.hasChanges(this, otherLine)) {
            return true
        }

        if (this.item !== undefined && otherLine.item !== undefined) {
            if (BaseRecord.hasChanges(this.item, otherLine.item)) {
                return true
            }
        }
        else if ((this.item === undefined) !== (otherLine.item === undefined)) {
            return true
        }

        return false
    }

    static trantypeToModel(trantype: string) {
        switch (trantype) {
            case 'booking':
                return Booking
            case 'charge':
                return Charge
            case 'estimate':
                return Estimate
            case 'package':
                return Package
            case 'invoice':
                return Invoice
            case 'gift':
                return Gift
            case 'adjustment':
                return Adjustment
            case 'subscription':
                return Subscription
            case 'order':
                return Order
            case 'royalty':
                return Royalty
            default:
                return undefined
        }
    }

    static modelToTrantype<ModelT extends Transaction>(model: ModelT): string | undefined {
        if (model instanceof Booking) {
            return 'booking'
        }
        else if (model instanceof Charge) {
            return 'charge'
        }
        else if (model instanceof Estimate) {
            return 'estimate'
        }
        else if (model instanceof Package) {
            return 'package'
        }
        else if (model instanceof Invoice) {
            return 'invoice'
        }
        else if (model instanceof Gift) {
            return 'gift'
        }
        else if (model instanceof Adjustment) {
            return 'adjustment'
        }
        else if (model instanceof Subscription) {
            return 'subscription'
        }
        else if (model instanceof Order) {
            return 'order'
        }
        else if (model instanceof Royalty) {
            return 'royalty'
        }

        return undefined
    }

    deserialize(data: any) {
        super.deserialize(data)

        if (data.item) this.item = BaseRecord.fromSerializer(data.item, Item)
        if (data.booking) this.booking = BaseRecord.fromSerializer(data.booking, Booking)
        if (data.trandoc && data.trantype) {
            const modelType = Line.trantypeToModel(data.trantype)
            if (modelType) {
                this.trandoc = BaseRecord.fromSerializer(
                    data.trandoc,
                    modelType as any
                ) as Transaction
            }
        }
        if (data.subaccount)
            this.subaccount = BaseRecord.fromSerializer(data.subaccount, Subaccount)
        if (data.job) this.job = BaseRecord.fromSerializer(data.job, Job)
        if (data.date) this.date = moment(data.date)
        if (data.lastModifiedDate) this.lastModifiedDate = moment(data.lastModifiedDate)
    }

    static fk: FKType = {
        bookings: ['booking'],
        dynamic: ['trandoc'],
        items: ['item'],
        jobs: ['job'],
        subaccounts: ['subaccount'],
    }

    static dataType(): DataType {
        return 'lines'
    }

    jsonObject(): any {
        const dict = super.jsonObject()
        dict['type'] = this.type
        dict['total'] = this.total ?? 0
        dict['taxable'] = this.taxable
        dict['discountable'] = this.discountable
        if (this.date) dict['date'] = formatTimeStamp(this.date)
        if (this.memo) dict['memo'] = this.memo
        if (this.booking) dict['booking'] = this.booking.id
        else if (this.job) dict['job'] = this.job.id
        else if (this.item) {
            dict['item'] = this.item.id
            dict['rate'] = this.rate
            dict['weight'] = this.weight
            dict['quantity'] = this.quantity !== undefined ? this.quantity : 1
        }
        else if (this.category) {
            dict['category'] = this.category
        }
        if (this.subaccount) dict['subaccount'] = this.subaccount.id
        return dict
    }

    get itemID(): string | undefined {
        return this.getForeignRecordID('item')
    }

    get item(): Item | undefined {
        return this.getForeignRecord('item', Item)
    }
    set item(item: Item | undefined) {
        this.setForeignRecord('item', item)
    }
    get booking(): Booking | undefined {
        return this.getForeignRecord('booking', Booking)
    }
    set booking(booking: Booking | undefined) {
        this.setForeignRecord('booking', booking)
    }
    get subaccount(): Subaccount | undefined {
        return this.getForeignRecord('subaccount', Subaccount)
    }
    set subaccount(subaccount: Subaccount | undefined) {
        this.setForeignRecord('subaccount', subaccount)
    }
    get job(): Job | undefined {
        return this.getForeignRecord('job', Job)
    }
    set job(job: Job | undefined) {
        this.setForeignRecord('job', job)
    }
    get trandoc(): Transaction | undefined {
        if (this.trantype === undefined) {
            return undefined
        }
        const model = Line.trantypeToModel(this.trantype)
        if (model === undefined) {
            return undefined
        }
        return this.getForeignRecord('trandoc', model as any)
    }
    set trandoc(trandoc: Transaction | undefined) {
        if (trandoc !== undefined) {
            this.trantype = Line.modelToTrantype(trandoc)
        }
        this.setForeignRecord('trandoc', trandoc)
    }
}

// See the comment on Transaction#getLineConstructor
;(window as any).PSLineModel = Line
